
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { AnimatedSection } from '@/hooks/useScrollAnimation';
import { useActiveVideos } from '@/hooks/useVideos';

const STORY_DURATION = 60; // segundos estimados por vídeo

// ── Thumbnail card — com detecção de tap vs arrasto ───────────────────────────
const ShortCard: React.FC<{ id: string; index: number; onClick: (i: number) => void }> = ({ id, index, onClick }) => {
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  return (
    <div
      className="relative w-full cursor-pointer rounded-xl overflow-hidden bg-dark-surface border border-white/8 hover:-translate-y-1 hover:border-gold/30 transition-all duration-300"
      style={{ aspectRatio: '9/16' }}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
      }}
      onTouchEnd={(e) => {
        const dx = Math.abs(e.changedTouches[0].clientX - touchStartX.current);
        const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
        if (dx < 15 && dy < 15) {
          e.preventDefault();
          onClick(index);
        }
      }}
      onClick={() => onClick(index)}
    >
      <img
        src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`}
        alt={`Vídeo ${index + 1}`}
        className="w-full h-full object-cover pointer-events-none"
        loading="lazy"
        decoding="async"
        width="480"
        height="360"
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center yt-pulse-btn">
          <svg width="15" height="15" viewBox="0 0 17 19" fill="white">
            <path d="M14.8756 7.71898C16.5188 8.6677 16.5188 11.0395 14.8756 11.9882L3.7837 18.3921C2.14046 19.3409 0.0864048 18.155 0.0864048 16.2575V3.44971C0.0864048 1.55226 2.14046 0.366349 3.7837 1.31508L14.8756 7.71898Z" />
          </svg>
        </div>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const VideoSection: React.FC = () => {
  const { videos: dbVideos, loading } = useActiveVideos();
  const videos = dbVideos.map(v => ({ id: v.youtube_id }));
  const useCarousel = videos.length > 5;

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState<number[]>([]);
  const [isMuted, setIsMuted] = useState(true);
  const [controlsVisible, setControlsVisible] = useState(true);

  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTime = useRef<number>(0);
  const currentIndexRef = useRef(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync progress array length with videos
  useEffect(() => {
    setProgress(videos.map(() => 0));
  }, [videos.length]); // eslint-disable-line

  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setControlsVisible(false), 3000);
  }, []);

  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const stopTimer = useCallback(() => {
    if (progressInterval.current) clearInterval(progressInterval.current);
  }, []);

  const buildSrc = useCallback((index: number) =>
    `https://www.youtube-nocookie.com/embed/${videos[index]?.id}?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1&controls=0&enablejsapi=1`,
  [videos]);

  const closeModal = useCallback(() => {
    if (iframeRef.current) iframeRef.current.src = '';
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setModalOpen(false);
    setIsMuted(true);
    setIsVideoPaused(false);
    setControlsVisible(true);
    stopTimer();
  }, [stopTimer]);

  const goNext = useCallback(() => {
    const idx = currentIndexRef.current;
    if (idx < videos.length - 1) {
      const next = idx + 1;
      setIsMuted(true); setIsVideoPaused(false);
      if (iframeRef.current) iframeRef.current.src = buildSrc(next);
      setProgress(prev => { const n = [...prev]; n[idx] = 100; return n; });
      setCurrentIndex(next);
    } else {
      closeModal();
    }
  }, [closeModal, buildSrc, videos.length]);

  const startTimer = useCallback(() => {
    stopTimer();
    startTime.current = Date.now();
    progressInterval.current = setInterval(() => {
      const elapsed = (Date.now() - startTime.current) / 1000;
      const pct = Math.min((elapsed / STORY_DURATION) * 100, 100);
      const idx = currentIndexRef.current;
      setProgress(prev => { const n = [...prev]; n[idx] = pct; return n; });
      if (pct >= 100) goNext();
    }, 100);
  }, [stopTimer, goNext]);

  const [isVideoPaused, setIsVideoPaused] = useState(false);

  const postCmd = useCallback((func: string) => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args: [] }), '*'
    );
  }, []);

  const unmuteIframe = useCallback(() => {
    postCmd('unMute');
    postCmd('playVideo');
  }, [postCmd]);

  const togglePlayPause = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isVideoPaused) { postCmd('playVideo'); setIsVideoPaused(false); }
    else               { postCmd('pauseVideo'); setIsVideoPaused(true); }
  }, [isVideoPaused, postCmd]);

  const goPrev = useCallback(() => {
    const idx = currentIndexRef.current;
    if (idx > 0) {
      const prev = idx - 1;
      setIsMuted(true); setIsVideoPaused(false);
      if (iframeRef.current) iframeRef.current.src = buildSrc(prev);
      setProgress(p => { const n = [...p]; n[idx] = 0; n[prev] = 0; return n; });
      setCurrentIndex(prev);
    } else {
      setIsMuted(true);
      if (iframeRef.current) iframeRef.current.src = buildSrc(0);
      setProgress(prev => { const n = [...prev]; n[0] = 0; return n; });
      startTimer();
    }
  }, [startTimer, buildSrc]);

  const openModal = useCallback((index: number) => {
    setIsMuted(true);
    if (iframeRef.current) iframeRef.current.src = buildSrc(index);
    setCurrentIndex(index);
    setProgress(videos.map((_, i) => (i < index ? 100 : 0)));
    setModalOpen(true);
    showControls();
  }, [showControls, buildSrc, videos]);

  // Reinicia timer ao mudar índice
  useEffect(() => {
    if (modalOpen) startTimer();
  }, [modalOpen, currentIndex]); // eslint-disable-line

  // Keyboard
  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modalOpen, closeModal, goNext, goPrev]);

  // Swipe no modal
  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  const onModalTouchStart = useCallback((e: React.TouchEvent) => {
    swipeStartX.current = e.touches[0].clientX;
    swipeStartY.current = e.touches[0].clientY;
  }, []);
  const onModalTouchEnd = useCallback((e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - swipeStartX.current;
    const dy = e.changedTouches[0].clientY - swipeStartY.current;
    if (Math.abs(dx) < 30 && Math.abs(dy) > 60) { closeModal(); return; }
    if (Math.abs(dx) > 50 && Math.abs(dy) < 60) {
      if (dx < 0) goNext(); else goPrev();
    }
  }, [closeModal, goNext, goPrev]);

  // Don't render section if no videos
  if (!loading && videos.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes yt-pulse {
          0%   { transform: scale(1);    box-shadow: 0 0 0 0 rgba(255,255,255,0.6); }
          70%  { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(255,255,255,0); }
          100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(255,255,255,0); }
        }
        .yt-pulse-btn { animation: yt-pulse 2s infinite; }
        .yt-modal-box {
          position: relative; background: #000; overflow: hidden;
          width: 100%; height: 100dvh; border-radius: 0;
        }
        @media (min-width: 768px) {
          .yt-modal-box {
            height: 90vh;
            width: calc(90vh * 9 / 16);
            border-radius: 1rem;
          }
        }
      `}</style>

      <section id="videos" aria-labelledby="videos-title" className="py-12 md:py-16 bg-dark-elevated relative overflow-hidden">
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] decorative-blur decorative-blur-primary opacity-10 pointer-events-none" aria-hidden="true" />

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <AnimatedSection>
            <header className="text-center mb-10 md:mb-12">
              <p className="subtitle-wide text-gold mb-3 block">Conteúdo</p>
              <h2 id="videos-title" className="text-3xl md:text-4xl text-light-primary tracking-tight leading-tight">
                Conheça um pouco sobre nosso trabalho
              </h2>
              <p className="text-light-muted text-base mt-3 max-w-2xl mx-auto leading-relaxed">
                Minha rotina envolve planejamento individualizado, acompanhamento próximo dos pacientes e produção de conteúdo educativo sobre cirurgia plástica.
              </p>
            </header>
          </AnimatedSection>

          <AnimatedSection delay={150}>
            <div className="max-w-7xl mx-auto relative">
              {useCarousel ? (
                <Carousel opts={{ align: 'start', loop: true }} className="w-full">
                  <CarouselContent className="-ml-4">
                    {videos.map((video, i) => (
                      <CarouselItem key={i} className="pl-4 basis-[60%] sm:basis-[40%] md:basis-1/3 lg:basis-1/4 xl:basis-1/5">
                        <ShortCard id={video.id} index={i} onClick={openModal} />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="carousel-arrow -left-5" />
                  <CarouselNext className="carousel-arrow -right-5" />
                </Carousel>
              ) : (
                <div
                  className={`grid gap-4 ${
                    videos.length === 1 ? 'grid-cols-1 max-w-[260px] mx-auto'
                    : videos.length === 2 ? 'grid-cols-2 max-w-xl mx-auto'
                    : videos.length === 3 ? 'grid-cols-2 sm:grid-cols-3 max-w-3xl mx-auto'
                    : videos.length === 4 ? 'grid-cols-2 md:grid-cols-4 max-w-4xl mx-auto'
                    : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5 max-w-5xl mx-auto'
                  }`}
                >
                  {videos.map((video, i) => (
                    <ShortCard key={i} id={video.id} index={i} onClick={openModal} />
                  ))}
                </div>
              )}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Story Modal ── */}
      <div className={`fixed inset-0 z-[70] bg-black/90 flex items-center justify-center transition-opacity duration-300 ${modalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>

        <div
          className="yt-modal-box"
          onTouchStart={onModalTouchStart}
          onTouchEnd={onModalTouchEnd}
          onClick={showControls}
        >

          <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/70 to-transparent z-30 pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/70 to-transparent z-30 pointer-events-none" />

          <div className="absolute top-3 left-3 right-3 z-40 flex gap-1.5">
            {videos.map((_, i) => (
              <div key={i} className="flex-1 h-[3px] bg-white/25 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full"
                  style={{ width: `${i < currentIndex ? 100 : i === currentIndex ? (progress[i] ?? 0) : 0}%`, transition: i === currentIndex ? 'width 0.1s linear' : 'none' }}
                />
              </div>
            ))}
          </div>

          <div className="absolute top-8 right-3 z-50">
            <button onClick={(e) => { e.stopPropagation(); closeModal(); }} aria-label="Fechar vídeo" className="text-white p-1.5 border border-white/40 hover:bg-white/20 rounded-full backdrop-blur-sm transition-colors flex items-center justify-center w-8 h-8">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {videos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                aria-label="Vídeo anterior"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-40 text-white p-2.5 bg-white/10 hover:bg-white/25 rounded-full backdrop-blur-sm transition-colors flex items-center justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6" /></svg>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                aria-label="Próximo vídeo"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-40 text-white p-2.5 bg-white/10 hover:bg-white/25 rounded-full backdrop-blur-sm transition-colors flex items-center justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            </>
          )}

          <div className="absolute bottom-6 left-0 right-0 z-40 flex items-center justify-center gap-4">
            <button onClick={togglePlayPause} aria-label={isVideoPaused ? 'Reproduzir vídeo' : 'Pausar vídeo'} className="text-white w-12 h-12 bg-white/15 hover:bg-white/25 border border-white/30 rounded-full backdrop-blur-sm transition-colors flex items-center justify-center">
              {isVideoPaused
                ? <svg width="18" height="18" viewBox="0 0 17 19" fill="white"><path d="M14.8756 7.71898C16.5188 8.6677 16.5188 11.0395 14.8756 11.9882L3.7837 18.3921C2.14046 19.3409 0.0864048 18.155 0.0864048 16.2575V3.44971C0.0864048 1.55226 2.14046 0.366349 3.7837 1.31508L14.8756 7.71898Z"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              }
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); if (isMuted) { setIsMuted(false); unmuteIframe(); } else { setIsMuted(true); postCmd('mute'); } }}
              aria-label={isMuted ? 'Ativar som' : 'Desativar som'}
              className={`w-12 h-12 rounded-full backdrop-blur-sm border transition-colors flex items-center justify-center ${isMuted ? 'bg-white text-dark-base border-white' : 'bg-white/15 hover:bg-white/25 border-white/30 text-white'}`}
              style={isMuted ? { animation: 'yt-pulse 2s infinite' } : {}}
            >
              {isMuted
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="1" x2="1" y2="23"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
              }
            </button>
          </div>

          <iframe
            ref={iframeRef}
            src=""
            title="Depoimento em vídeo de paciente"
            width="1280"
            height="720"
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </>
  );
};

export default VideoSection;
